import { io, type Socket } from 'socket.io-client';
import { ENV } from '@/lib/config/env';
import { getAccessToken, refreshTokens } from '@/lib/auth';

/**
 * Singleton notifications socket. Lives at module scope (not in React state) so
 * it survives component remounts and there is ever only one connection per tab.
 *
 * Authorization is entirely server-side: the handshake carries the access token,
 * the backend validates it and joins the socket to permission/scope rooms. The
 * client never asks to join a room, so it can only receive notifications it is
 * authorized for.
 */
let socket: Socket | null = null;

/** Derive the socket origin from the REST base URL (strip the `/api` path). */
function socketOrigin(): string {
  try {
    return new URL(ENV.VITE_API_BASE_URL).origin;
  } catch {
    return ENV.VITE_API_BASE_URL;
  }
}

export function getSocket(): Socket {
  if (socket) return socket;

  socket = io(`${socketOrigin()}/notifications`, {
    transports: ['websocket'],
    autoConnect: false,
    // A callback (not a static value) is re-evaluated on every (re)connect, so
    // after refreshTokens() writes a new access token the next reconnect uses it.
    auth: (cb) => cb({ token: getAccessToken() ?? '' }),
  });

  // Mirror the REST 401 → refresh → retry flow: if the server rejects the
  // handshake, try one token refresh then reconnect; give up (and tear down) if
  // that fails so we don't loop against a revoked session.
  socket.on('unauthorized', async () => {
    try {
      await refreshTokens();
      socket?.connect();
    } catch {
      disconnectSocket();
    }
  });

  return socket;
}

export function connectSocket(): void {
  const s = getSocket();
  if (!s.connected) s.connect();
}

export function disconnectSocket(): void {
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
}
